import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: var(--bg-dark);
`;

const PricingContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem 1.5rem 4rem;
  color: var(--text-light);
  flex: 1;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  color: var(--text-light);
  text-align: center;
`;

const Description = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  margin-bottom: 3rem;
  text-align: center;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  color: rgba(255, 255, 255, 0.8);
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  max-width: 800px;
  margin: 0 auto;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const PricingCard = styled.div`
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  overflow: hidden;
  border: ${({ isPremium }) => isPremium ? '2px solid var(--primary)' : '2px solid rgba(255, 255, 255, 0.1)'};
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
  }
`;

const PopularTag = styled.div`
  position: absolute;
  top: 30px;
  right: -75px;
  width: 250px;
  height: 35px;
  background-color: var(--primary);
  color: #000;
  font-size: 0.85rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: rotate(45deg);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.25);
  z-index: 1;
`;

const PlanName = styled.h3`
  margin-bottom: 1.5rem;
  text-align: center;
  color: var(--text-light);
  font-size: 1.8rem;
`;

const PriceContainer = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  margin-bottom: 2rem;
  font-weight: 700;
  color: var(--text-light);
`;

const Price = styled.span`
  font-size: 3rem;
  line-height: 1;
`;

const Period = styled.span`
  color: rgba(255, 255, 255, 0.6);
  margin-left: 0.5rem;
`;

const FeaturesList = styled.ul`
  list-style: none;
  margin-bottom: 2rem;
  flex-grow: 1;
`;

const Feature = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  color: rgba(255, 255, 255, 0.8);
  
  span:first-child {
    color: ${({ included }) => included ? 'var(--primary)' : '#ff4d4d'};
    margin-right: 0.8rem;
    font-size: 1.2rem;
  }
`;

const FreeTrialText = styled.p`
  font-size: 0.9rem;
  text-align: center;
  margin-bottom: 1.5rem;
  color: var(--primary);
  font-weight: 500;
`;

const PricingButton = styled.a`
  display: inline-block;
  padding: 0.9rem 1.5rem;
  background-color: ${({ isPrimary }) => isPrimary ? 'var(--primary)' : 'transparent'};
  color: ${({ isPrimary }) => isPrimary ? '#000' : 'var(--primary)'};
  border: 2px solid var(--primary);
  border-radius: 8px;
  text-align: center;
  font-weight: 600;
  transition: all 0.3s ease;
  text-decoration: none;
  width: 100%;
  
  &:hover {
    background-color: ${({ isPrimary }) => isPrimary ? '#00ff9d' : 'var(--primary)'};
    color: #000;
    transform: translateY(-2px);
  }
`;

const PolicyLink = styled.a`
  color: var(--primary);
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const PricingPage = () => {
  const { t, i18n } = useTranslation();
  
  // Общие функции для всех тарифов
  const commonFeatures = [
    { 
      id: 'aiRecognition', 
      name: {
        en: 'Advanced AI food recognition',
        ru: 'Продвинутое распознавание продуктов питания с помощью ИИ',
        fr: 'Reconnaissance alimentaire avancée par IA',
        es: 'Reconocimiento avanzado de alimentos con IA',
        de: 'Fortschrittliche KI-Lebensmittelerkennung'
      }
    },
    { 
      id: 'nutritionTracking', 
      name: {
        en: 'Nutrition tracking with calories, protein, fats, carbs',
        ru: 'Ведение дневника питания с подсчетом КБЖУ',
        fr: 'Suivi nutritionnel avec calories, protéines, lipides, glucides',
        es: 'Seguimiento nutricional con calorías, proteínas, grasas, carbohidratos',
        de: 'Ernährungsverfolgung mit Kalorien, Protein, Fett und Kohlenhydraten'
      }
    },
    { 
      id: 'unlimitedScans', 
      name: {
        en: 'Unlimited meal scans',
        ru: 'Неограниченное количество сканирований приема пищи',
        fr: 'Analyses de repas illimitées',
        es: 'Escaneos de comidas ilimitados',
        de: 'Unbegrenzte Mahlzeiten-Scans'
      }
    },
    { 
      id: 'reports', 
      name: {
        en: 'Weekly & monthly reports',
        ru: 'Еженедельные и ежемесячные отчеты',
        fr: 'Rapports hebdomadaires et mensuels',
        es: 'Informes semanales y mensuales',
        de: 'Wöchentliche und monatliche Berichte'
      }
    },
    { 
      id: 'aiEditing', 
      name: {
        en: 'AI meal editing',
        ru: 'ИИ редактирование блюд',
        fr: 'Édition de repas par IA',
        es: 'Edición de comidas con IA',
        de: 'KI-Mahlzeitenbearbeitung'
      }
    },
    { 
      id: 'personalGoals', 
      name: {
        en: 'Personalized nutrition goals based on your body metrics',
        ru: 'Расчет нормы КБЖУ в зависимости от целей и параметров организма',
        fr: 'Objectifs nutritionnels personnalisés en fonction de vos métriques corporelles',
        es: 'Objetivos nutricionales personalizados según las métricas de tu cuerpo',
        de: 'Personalisierte Ernährungsziele basierend auf Ihren Körpermetriken'
      }
    }
  ];
  
  // Получаем текущий язык
  const currentLang = i18n.language || 'en';
  // Проверяем, есть ли такой язык в списке поддерживаемых, если нет, используем английский
  const lang = ['en', 'ru', 'fr', 'es', 'de'].includes(currentLang) ? currentLang : 'en';
  
  const plans = [
    {
      name: t('pricing.monthly', 'Monthly'),
      price: '7.99',
      features: commonFeatures.map(feature => ({
        name: feature.name[lang],
        included: true
      })),
      buttonText: t('pricing.monthlySubscribe', 'Subscribe Monthly'),
      buttonLink: '#download',
      isPremium: true,
      freeTrial: false
    },
    {
      name: t('pricing.annual', 'Annual'),
      price: '34.99',
      pricePerMonth: '2.92',
      features: commonFeatures.map(feature => ({
        name: feature.name[lang],
        included: true
      })),
      buttonText: t('pricing.annualSubscribe', 'Subscribe Yearly'),
      buttonLink: '#download',
      isPremium: true,
      isPopular: true,
      freeTrial: true,
      discount: t('pricing.discount', '63% off')
    }
  ];

  return (
    <PageContainer>
      <PricingContainer>
        <Title>{t('pricing.chooseYourPlan', 'Choose Your Plan')}</Title>
        <Description>
          {t('pricing.description', 'Get unlimited access to AI food recognition, detailed nutrition tracking, and personalized insights to help you achieve your health goals.')}
        </Description>
        
        <PricingGrid>
          {plans.map((plan, index) => (
            <PricingCard key={index} isPremium={plan.isPremium}>
              {plan.isPopular && <PopularTag>{plan.discount}</PopularTag>}
              <PlanName>{plan.name}</PlanName>
              <PriceContainer>
                <Price>${plan.price}</Price>
                <Period>{plan.name === t('pricing.annual', 'Annual') ? t('pricing.perYear', '/year') : t('pricing.perMonth', '/month')}</Period>
              </PriceContainer>
              {plan.pricePerMonth && (
                <FreeTrialText>{t('pricing.onlyPerMonth', { price: plan.pricePerMonth })}</FreeTrialText>
              )}
              {plan.freeTrial && (
                <FreeTrialText>{t('pricing.freeTrial', 'Includes 3-day free trial')}</FreeTrialText>
              )}
              <FeaturesList>
                {plan.features.map((feature, i) => (
                  <Feature key={i} included={feature.included}>
                    <span>{feature.included ? '✓' : '✕'}</span>
                    <span>{feature.name}</span>
                  </Feature>
                ))}
              </FeaturesList>
              <PricingButton 
                href={plan.buttonLink} 
                isPrimary={plan.isPremium}
              >
                {plan.buttonText}
              </PricingButton>
            </PricingCard>
          ))}
        </PricingGrid>
        
        <Description style={{ marginTop: '3rem', fontSize: '0.9rem', opacity: '0.7' }}>
          {t('pricing.disclaimer', 'Prices are in USD. By subscribing, you agree to our')} <PolicyLink href="/subscription">{t('footer.subscription', 'Subscription Terms')}</PolicyLink>. 
          {t('pricing.refundInfo', 'For more information about our refund policy, please visit our')} <PolicyLink href="/refund">{t('footer.refund', 'Refund Policy')}</PolicyLink>.
        </Description>
      </PricingContainer>
    </PageContainer>
  );
};

export default PricingPage; 